import { AUTHENTICATE_STATUS, defaultPaging } from '@/common/constants/ConstCode';
import { isEmpty } from '@/common/utils/Util';

// 중복 필터링: DB 비즈니스는 businessNo 기반, 카카오맵 데이터는 이름 기반으로 기존 DB 업체와 중복 제외
const filterDuplicates = (existingList, newList) => {
  const existingIds = new Set(existingList.filter(item => item.businessNo).map(item => item.businessNo));
  const existingNames = new Set(existingList.filter(item => item.businessNo).map(item => (item.name || '').trim()));
  return newList.filter(item => {
    // DB 비즈니스: businessNo로 중복 체크
    if (item.businessNo) {
      if (existingIds.has(item.businessNo)) return false;
      existingIds.add(item.businessNo);
      return true;
    }
    // 카카오맵 데이터: 이름으로 DB 업체와 중복 체크
    const name = (item.name || item.place_name || '').trim();
    if (!name) return true;
    if (existingNames.has(name)) return false;
    existingNames.add(name);
    return true;
  });
};

export default {
  businessList: (state, { isFlag = false, businessList = [], expectedUserNo = null }) => {
    if (isEmpty(businessList)) state.businessList = [];
    else {
      // 260429: owner 가드 — expectedUserNo 지정 시 그 owner 행만 받아들임 (메인↔마이페이지 race 누수 차단)
      const guarded = expectedUserNo != null
        ? businessList.filter(item => !item.businessNo || Number(item.userNo) === Number(expectedUserNo))
        : businessList;
      const filtered = filterDuplicates(state.businessList, guarded);
      state.businessList = [...state.businessList, ...filtered];
    }
    if (isFlag) {
      state.businessList.sort((a, b) => {
        const statusOrder = {
          SUCCESS: 1,
          WAITING: 2,
          FAIL: 3
        };
        const getOrder = status => statusOrder[status] ?? 3;

        const diff = getOrder(a.authenticateStatus) - getOrder(b.authenticateStatus);
        if (diff !== 0) {
          return diff;
        }
        return a.distance - b.distance;
      });
    }
  },
  likeList: (state, likeList = []) => {
    if (isEmpty(likeList)) state.likeList = [];
    else state.likeList = [...state.likeList, ...likeList];
  },
  purchaseList: (state, purchaseList = []) => {
    if (isEmpty(purchaseList)) state.purchaseList = [];
    else state.purchaseList = [...state.purchaseList, ...purchaseList];
  },
  businessSearchList: (state, businessSearchList = []) => {
    if (isEmpty(businessSearchList)) state.businessSearchList = [];
    else {
      const filtered = filterDuplicates(state.businessSearchList, businessSearchList);
      state.businessSearchList = [...state.businessSearchList, ...filtered];
    }

    state.businessSearchList.sort((a, b) => {
      const statusOrder = {
        SUCCESS: 1,
        WAITING: 2,
        FAIL: 3
      };
      const getOrder = status => statusOrder[status] ?? 3; // 없는 값은 제일 뒤로 밀림
      const diff = getOrder(a.authenticateStatus) - getOrder(b.authenticateStatus);
      if (diff !== 0) {
        return diff; // status 기준 정렬
      }
      return a.distance - b.distance;
    });
  },
  businessPreviewList: (state, businessPreviewList = []) => (state.businessPreviewList = businessPreviewList),
  signboardFeedList: (state, list = []) => (state.signboardFeedList = list),
  signboardDetail: (state, detail = {}) => (state.signboardDetail = detail ?? {}),
  setSignboardChargeMode: (state, businessNo = null) => {
    state.signboardChargeMode = businessNo;
    try {
      if (businessNo) localStorage.setItem('signboardChargeMode', String(businessNo));
      else localStorage.removeItem('signboardChargeMode');
    } catch (e) { /* ignore */ }
  },
  categoryList: (state, categoryList = []) => (state.categoryList = categoryList),
  addressList: (state, addressList = []) => (state.addressList = addressList),
  productList: (state, productList = []) => (state.productList = productList),

  marketingTicketCount: (state, marketingTicketCount = 0) => (state.marketingTicketCount = marketingTicketCount),
  markerPin: (state, markerPin = {}) => (state.markerPin = markerPin),
  isLocationModal: (state, isLocationModal = true) => (state.isLocationModal = isLocationModal),
  registerPin: (state, registerPin = {}) => (state.registerPin = registerPin),
  setDetail: (state, detail = {}) => (state.detail = detail),
  setDetailSubscribed: (state, isSubscribed) => {
    state.detail = { ...(state.detail || {}), isSubscribed: !!isSubscribed };
  },
  setJobDetail: (state, jobDetail = {}) => (state.jobDetail = jobDetail),
  setCategorySearchFlag: (state, categorySearchFlag = false) => (state.categorySearchFlag = categorySearchFlag),
  setOverseasFilter: (state, v = false) => (state.overseasFilter = v),
  setTicketCount: (state, count) => {
    state.detail.ticketMax = count.ticketMax;
    state.detail.ticketUsed = count.ticketUsed;
  },
  selectAddress: (state, selectAddress = { address: '서울 서초구 서초동' }) => (state.selectAddress = selectAddress),
  setRegister: (state, regData) => Object.assign(state.register, regData),
  setMarketingRegister: (state, regData) => (state.marketingRegister = regData),
  setDonationRegister: (state, regData) => (state.donationRegister = regData),
  setJobRegister: (state, regData) => (state.jobRegister = regData),

  initBusiness: state => {
    state.condition = { userNo: '', searchText: '', ...defaultPaging };
    state.selectAddress = { address: '서울 서초구 서초동' };
    state.businessList = [];
    state.businessSearchList = [];
    state.kakaoPageableCount = 0;
    state.currentKakaoCount = 0;
    state.kakaoSearchText = null;
    state.pageInfo = { ...defaultPaging };
    state.ghostList = [];
  },
  // 260522: 네이버 미가입 ghost 카드 (똑딱 모델). searchText page=1 호출 응답에서만 채움
  ghostList: (state, list = []) => (state.ghostList = Array.isArray(list) ? list : []),
  // 260429: 마이페이지 전용 격리
  myBusinessList: (state, { myBusinessList = [], expectedUserNo = null } = {}) => {
    if (isEmpty(myBusinessList)) {
      state.myBusinessList = [];
      return;
    }
    const guarded = expectedUserNo != null
      ? myBusinessList.filter(item => !item.businessNo || Number(item.userNo) === Number(expectedUserNo))
      : myBusinessList;
    const existingIds = new Set(state.myBusinessList.map(i => i.businessNo).filter(Boolean));
    const merged = guarded.filter(i => !existingIds.has(i.businessNo));
    state.myBusinessList = [...state.myBusinessList, ...merged];
  },
  // 260429: 부분 갱신용 (티켓 사용/재구매 후 imageUsed 등 일부 필드만 바꿔치기)
  setMyBusinessList: (state, list = []) => (state.myBusinessList = list),
  myCondition: (state, myCondition = { userNo: '', ...defaultPaging }) =>
    Object.assign(state.myCondition, myCondition),
  myPageInfo: (state, myPageInfo = { ...defaultPaging }) => (state.myPageInfo = myPageInfo),
  initMyBusiness: state => {
    state.myCondition = { userNo: '', ...defaultPaging };
    state.myBusinessList = [];
    state.myPageInfo = { ...defaultPaging };
  },
  initBusinessList: state => {
    // 검색어 , 현재 주소가 변경되면 안될경우
    state.condition.page = 1;
    state.businessList = [];
    state.businessSearchList = [];
    state.kakaoPageableCount = 0;
    state.currentKakaoCount = 0;
    state.kakaoSearchText = null;
    state.pageInfo = { ...defaultPaging };
    state.ghostList = [];
  },

  initLike: state => {
    state.likeCondition = { userNo: '', ...defaultPaging };
    state.likeList = [];
    state.likePageInfo = { ...defaultPaging };
  },
  initPurchase: state => {
    state.purchaseCondition = { userNo: '', ...defaultPaging };
    state.purchaseList = [];
    state.purchasePageInfo = { ...defaultPaging };
  },
  addressSearchText: (state, addressSearchText = '') => {
    state.addressSearchText = addressSearchText;
  },
  condition: (state, condition = { userNo: '', searchText: '', ...defaultPaging }) =>
    Object.assign(state.condition, condition),
  addressCondition: (state, addressCondition = { userNo: '', ...defaultPaging }) =>
    Object.assign(state.addressCondition, addressCondition),
  likeCondition: (state, likeCondition = { userNo: '', ...defaultPaging }) =>
    Object.assign(state.likeCondition, likeCondition),
  purchaseCondition: (state, purchaseCondition = { userNo: '', ...defaultPaging }) =>
    Object.assign(state.purchaseCondition, purchaseCondition),
  conditionPage: (state, page = 1) => (state.condition.page = page),
  kakaoPageableCount: (state, kakaoPageableCount = 0) => {
    state.kakaoPageableCount = Math.ceil(kakaoPageableCount / 10);
  },
  kakaoSearchText: (state, kakaoSearchText = null) => (state.kakaoSearchText = kakaoSearchText),

  pageInfo: (state, pageInfo = { ...defaultPaging }) => (state.pageInfo = pageInfo),
  likePageInfo: (state, likePageInfo = { ...defaultPaging }) => (state.likePageInfo = likePageInfo),
  addressPageInfo: (state, addressPageInfo = { ...defaultPaging }) => (state.addressPageInfo = addressPageInfo),
  purchasePageInfo: (state, purchasePageInfo = { ...defaultPaging }) => (state.purchasePageInfo = purchasePageInfo),

  searchText: (state, searchText = '') => (state.searchText = searchText),
  coordinateChange: (state, coordinateChange = false) => (state.coordinateChange = coordinateChange),

  coordinate: (state, coordinate = { latitude: 37.4986655, longitude: 127.027028, isOneKM: true }) =>
    (state.coordinate = coordinate),
  // 260618: GPS 실위치 좌표 (getSelectAddress의 대표주소 덮어쓰기와 무관하게 유령기업 카카오 검색에 사용)
  gpsCoordinate: (state, gpsCoordinate = null) => (state.gpsCoordinate = gpsCoordinate),
  fixCoordinate: (state, fixCoordinate = { latitude: 37.4986655, longitude: 127.027028 }) =>
    (state.fixCoordinate = fixCoordinate),
  recentSearch: (state, recentSearch = []) => (state.recentSearch = recentSearch),
  initMarketingRegister: (
    state,
    marketingRegister = { businessNo: '', title: '', contents: '', imageList: [], eventStartDate: '', eventEndDate: '' }
  ) => (state.marketingRegister = marketingRegister),
  initRegister: (
    state,
    register = {
      userNo: '',
      businessNo: '',
      isAgreeMarketing: false,
      businessCategoryNo: '',
      name: '',
      userSelectedType: '',
      address: '',
      roadAddress: '',
      addressDetail: '',
      imageList: [],
      tags: [],
      isGlobal: false,
      licenseNumber: '',
      authenticateStatus: AUTHENTICATE_STATUS.WAITING.key,
      openTime: '',
      closeTime: '',
      offDays: [],
      phone: '',
      introduce: '',
      logoImage: null,
      latitude: '',
      longitude: '',
      buildingName: ''
    }
  ) => (state.register = register)
};
